import { CREATOR_EMAIL_RULES } from "@artha/types";

/** Validates a bare sending hostname while leaving room for a maximum-length local part. */
export function normalizeCreatorEmailDomain(domain: string): string {
  const normalized = domain.trim().toLowerCase();
  if (normalized.length > CREATOR_EMAIL_RULES.domainMaxLength || !CREATOR_EMAIL_RULES.domainPattern.test(normalized)) {
    throw new Error("CREATOR_EMAIL_DOMAIN must be a valid email domain without a scheme, path or port");
  }
  return normalized;
}

/** Lowercases and trims a username; rejects unsupported characters instead of merging identities. */
export function getCreatorEmailIdentity(username: string, domain: string = CREATOR_EMAIL_RULES.defaultDomain) {
  const senderDomain = normalizeCreatorEmailDomain(domain);
  const creatorEmailUsername = username.trim().toLowerCase();
  if (
    creatorEmailUsername.length > CREATOR_EMAIL_RULES.localPartMaxLength ||
    !CREATOR_EMAIL_RULES.usernamePattern.test(creatorEmailUsername) ||
    CREATOR_EMAIL_RULES.reservedUsernames.has(creatorEmailUsername)
  )
    return null;
  return {
    username: creatorEmailUsername,
    address: `${creatorEmailUsername}@${senderDomain}`,
  };
}

/** Returns the current sender fields or rejects an unsupported username. */
export function getCreatorEmailFields(username: string, domain: string = CREATOR_EMAIL_RULES.defaultDomain) {
  const identity = getCreatorEmailIdentity(username, domain);
  if (!identity)
    throw new Error("Username cannot be used as a creator email sender");
  return identity;
}

/** Normalizes a legacy handle; callers allocate an available numeric suffix before storing it. */
export function generateCreatorEmailFields(username: string, sequence?: number, domain: string = CREATOR_EMAIL_RULES.defaultDomain) {
  if (sequence !== undefined && (
    !Number.isSafeInteger(sequence) || sequence < CREATOR_EMAIL_RULES.firstCollisionSuffix
  )) throw new Error("Creator email suffix must be an integer of at least 2");
  let base = username.trim().toLowerCase()
    .replace(CREATOR_EMAIL_RULES.unsupportedCharactersPattern, "_")
    .replace(CREATOR_EMAIL_RULES.repeatedSeparatorsPattern, "_")
    .replace(CREATOR_EMAIL_RULES.edgeSeparatorsPattern, "") || CREATOR_EMAIL_RULES.defaultUsername;
  if (CREATOR_EMAIL_RULES.reservedUsernames.has(base)) {
    base = `${CREATOR_EMAIL_RULES.defaultUsername}_${base}`;
  }
  const suffix = sequence === undefined ? "" : `_${sequence}`;
  const stem = base.slice(0, CREATOR_EMAIL_RULES.localPartMaxLength - suffix.length)
    .replace(CREATOR_EMAIL_RULES.edgeSeparatorsPattern, "");
  return getCreatorEmailFields(`${stem}${suffix}`, domain);
}
